/**
 * Scoring logic for Belgian Colour Whist.
 *
 * All table values represent the per-player settlement unit:
 *   - Samen / Troel (2 v 2): each partner ± V, each opponent ∓ V
 *   - Solo contracts (1 v 3): soloist ± 3V, each opponent ∓ V
 *   - Misery (multiple): each contract player settled independently as solo,
 *     results summed
 *
 * Reference: https://www.whisthub.com/rules#the-scoring
 */

// ── Samen (Accept) ──────────────────────────────────────────────────

const SOLO_BASES = { 5: 3, 6: 4, 7: 5, 8: 7 }

const ABONDANCE_VALUES = { 9: 10, 10: 15, 11: 20, 12: 30 }

const MISERY_VALUES = {
  'kleine-miserie': 6,
  'piccolo': 8,
  'grote-miserie': 12,
  'open-miserie': 24,
}

/**
 * Samen (Accept) table value.
 *   Success (tricksTaken >= bid): value based on tricks taken.
 *   Failure (tricksTaken < bid): negative, scaled by undertricks.
 */
function samenValue(bid, tricksTaken) {
  if (tricksTaken >= bid) {
    // Overtrick / success value depends only on tricks taken
    return tricksTaken === 13 ? 30 : 3 * tricksTaken - 16
  }
  // Failure: -(regularBase + 3 × undertricks)
  const regularBase = 3 * bid - 16
  return -(regularBase + 3 * (bid - tricksTaken))
}

/**
 * Alleen (Solo) table value.
 *   Max scoring tricks = 8.
 */
function alleenValue(bid, tricksTaken) {
  const base = SOLO_BASES[bid]
  const effective = Math.min(tricksTaken, 8)
  if (effective >= bid) {
    return base + (effective - bid)
  }
  return -(base + (bid - tricksTaken))
}

/**
 * Abondance table value.
 *   Success value depends on tricks taken (not bid).
 *   Failure is a flat penalty equal to the bid's base.
 */
function abondanceValue(bid, tricksTaken) {
  if (tricksTaken >= bid) {
    const effective = Math.min(tricksTaken, 12)
    return ABONDANCE_VALUES[effective]
  }
  return -ABONDANCE_VALUES[bid]
}

/**
 * Troel table value.
 *   +16 on success, +30 on slam (all 13), −16 on failure.
 */
function troelValue(bid, tricksTaken) {
  if (tricksTaken >= bid) {
    return tricksTaken === 13 ? 30 : 16
  }
  return -16
}

// ── Distribution helpers ─────────────────────────────────────────────

/**
 * Team contract (2 v 2): each partner gets ± V, each opponent gets ∓ V.
 */
function distributeTeam(value, contractPlayerIds, allPlayerIds) {
  const contractSet = new Set(contractPlayerIds)
  const scores = {}
  for (const id of allPlayerIds) {
    scores[id] = contractSet.has(id) ? value : -value
  }
  return scores
}

/**
 * Solo contract (1 v 3): soloist gets ± 3V, each opponent gets ∓ V.
 */
function distributeSolo(value, soloistId, allPlayerIds) {
  const scores = {}
  for (const id of allPlayerIds) {
    scores[id] = id === soloistId ? 3 * value : -value
  }
  return scores
}

/**
 * Misery with potentially multiple contract players.
 * Each contract player is settled independently as a solo vs the other 3.
 * Results are summed.
 */
function distributeMisery(baseValue, contractPlayerIds, playerResults, allPlayerIds) {
  const scores = {}
  for (const id of allPlayerIds) {
    scores[id] = 0
  }

  for (const cpId of contractPlayerIds) {
    const succeeded = playerResults[cpId]
    const value = succeeded ? baseValue : -baseValue
    // This contract player's solo settlement
    scores[cpId] += 3 * value
    for (const id of allPlayerIds) {
      if (id !== cpId) {
        scores[id] -= value
      }
    }
  }

  return scores
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Calculate score deltas for every player given a contract and its result.
 *
 * @param {Object} contract  – contractState from the store
 * @param {Object} result    – { tricksTaken, singleResult, playerResults }
 * @param {Array}  allPlayers – full players array [{ id, … }, …]
 * @returns {Object} map of playerId → score delta
 */
export function calculateScores(contract, result, allPlayers) {
  const allIds = allPlayers.map(p => p.id)
  const type = contract.type
  const bid = contract.tricks
  const contractPlayerIds = contract.playerIds

  switch (type) {
    case 'samen': {
      const v = samenValue(bid, result.tricksTaken)
      return distributeTeam(v, contractPlayerIds, allIds)
    }

    case 'alleen': {
      const v = alleenValue(bid, result.tricksTaken)
      return distributeSolo(v, contractPlayerIds[0], allIds)
    }

    case 'abondance': {
      const v = abondanceValue(bid, result.tricksTaken)
      return distributeSolo(v, contractPlayerIds[0], allIds)
    }

    case 'troel': {
      const v = troelValue(bid, result.tricksTaken)
      return distributeTeam(v, contractPlayerIds, allIds)
    }

    case 'kleine-miserie':
    case 'piccolo':
    case 'grote-miserie':
    case 'open-miserie': {
      const base = MISERY_VALUES[type]
      return distributeMisery(base, contractPlayerIds, result.playerResults, allIds)
    }

    case 'solo-slim': {
      const v = result.singleResult ? 60 : -60
      return distributeSolo(v, contractPlayerIds[0], allIds)
    }

    default:
      return Object.fromEntries(allIds.map(id => [id, 0]))
  }
}
